import useSourceId from '../../../v2/hooks/useSourceId';
import { SmartPropertyCitationPopover } from 'property-value-citations/public/SmartPropertyCitationPopover';
import { useState, useMemo } from 'react';
import { Text } from 'foundations-components/components/Text';
import { parsePropertySourceIdSmartPropertyResultId, parsePropertySourceSmartDataCaptureSuggestionId } from 'customer-data-objects/property/PropertySourceIdentifier';
import { BreezeSingleStarIcon } from 'foundations-assets/icons/components/BreezeSingleStarIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Button } from 'foundations-components/transitional/components/Button';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useHttpClient } from '../../../client/HttpClientContext';
import { useGetIsUngated } from '../../../v2/hooks/useGetIsUngated';
import { gate } from 'hub-http/gates';
import { SmartDataCaptureCitationPopover } from 'property-value-citations/public/SmartDataCaptureCitationPopover';

/**
 * This JSX is a hook instead of a component because we need to check conditionally whether the rendered result is truthy/falsy,
 * in order to be able to not pass content to FormControl's hoverContent prop.
 *
 * Were this a component, its 'value' would always be truthy and we would wind up passing empty, but present, hover content.
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const useGetSourceCitationHoverContent = () => {
  const [isSmartPropertyCitationPopoverOpen, setIsSmartPropertyCitationPopoverOpen] = useState(false);
  const [isSmartDataCaptureCitationPopoverOpen, setIsSmartDataCaptureCitationPopoverOpen] = useState(false);
  const {
    data: sourceId
  } = useSourceId();
  const parsedSmartPropertySourceId = parsePropertySourceIdSmartPropertyResultId(sourceId);
  const parsedSmartDataCaptureSuggestionIdSourceId = parsePropertySourceSmartDataCaptureSuggestionId(sourceId);
  const getIsUngated = useGetIsUngated();
  const isUngatedForSmartDataCaptureCitations = getIsUngated(gate('CRM:Properties:SmartDataCapture:Citations'));
  const httpClient = useHttpClient();
  return useMemo(() => {
    const handleSmartPropertyCitationPopoverOnOpenChange = event => {
      setIsSmartPropertyCitationPopoverOpen(event.target.value);
    };
    const handleSmartPropertyCitationButtonClick = () => {
      setIsSmartPropertyCitationPopoverOpen(prev => !prev);
    };
    const handleSmartDataCaptureCitationPopoverOnOpenChange = event => {
      setIsSmartDataCaptureCitationPopoverOpen(event.target.value);
    };
    const handleSmartDataCaptureCitationButtonClick = () => {
      setIsSmartDataCaptureCitationPopoverOpen(prev => !prev);
    };
    if (parsedSmartPropertySourceId) {
      return /*#__PURE__*/_jsx(SmartPropertyCitationPopover, {
        smartPropertyResultId: parsedSmartPropertySourceId,
        onOpenChange: handleSmartPropertyCitationPopoverOnOpenChange,
        open: isSmartPropertyCitationPopoverOpen,
        children: /*#__PURE__*/_jsx(Button, {
          use: "link",
          size: "xs",
          onClick: handleSmartPropertyCitationButtonClick,
          children: /*#__PURE__*/_jsxs(Flex, {
            gap: "50",
            alignItems: "center",
            children: [/*#__PURE__*/_jsx(BreezeSingleStarIcon, {
              color: "fill-accent-magenta-default"
            }), /*#__PURE__*/_jsx(Text, {
              color: "fill-accent-magenta-default",
              typography: "heading-25",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "customerDataProperties.formControl.AI"
              })
            })]
          })
        })
      });
    } else if (parsedSmartDataCaptureSuggestionIdSourceId && isUngatedForSmartDataCaptureCitations) {
      return /*#__PURE__*/_jsx(SmartDataCaptureCitationPopover, {
        httpClient: httpClient,
        smartDataCaptureSuggestionId: parsedSmartDataCaptureSuggestionIdSourceId.suggestionId,
        onOpenChange: handleSmartDataCaptureCitationPopoverOnOpenChange,
        open: isSmartDataCaptureCitationPopoverOpen,
        children: /*#__PURE__*/_jsx(Button, {
          use: "link",
          size: "xs",
          onClick: handleSmartDataCaptureCitationButtonClick,
          children: /*#__PURE__*/_jsxs(Flex, {
            gap: "50",
            alignItems: "center",
            children: [/*#__PURE__*/_jsx(BreezeSingleStarIcon, {
              color: "fill-accent-magenta-default"
            }), /*#__PURE__*/_jsx(Text, {
              color: "fill-accent-magenta-default",
              typography: "heading-25",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "customerDataProperties.formControl.AI"
              })
            })]
          })
        })
      });
    } else {
      return null;
    }
  }, [parsedSmartPropertySourceId, parsedSmartDataCaptureSuggestionIdSourceId, isUngatedForSmartDataCaptureCitations, isSmartPropertyCitationPopoverOpen, httpClient, isSmartDataCaptureCitationPopoverOpen]);
};